import {
  type BulkItem,
  type BulkItemResult,
  settleEach,
} from 'src/utils/bulkResults';
import {
  AggregateAccountDetailsQuery,
  AggregateAccountDetailsQueryOutput,
  LinkUserByEmailInput,
  SearchAccountOutput,
  SearchAccountQuery,
} from 'switch-shared/modules/crud/account/account.dto';
import { Account } from 'switch-shared/modules/crud/account/account.entity';
import type {
  BulkRenameItem,
  BulkRenameResult,
} from 'switch-shared/modules/crud/base/base.dto';
import {
  CreateDto,
  UpdateDto,
} from 'switch-shared/modules/crud/base/base.service';
import { User } from 'switch-shared/modules/crud/user/user.entity';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const accountAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [ENDPOINT_URLS.ACCOUNT],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAccountById: builder.query<Account, string>({
        query: (id) => `${ENDPOINT_URLS.ACCOUNT}/${id}`,
        providesTags: (_res, _err, id) => [{ type: ENDPOINT_URLS.ACCOUNT, id }],
      }),

      getAllAccounts: builder.query<SearchAccountOutput, SearchAccountQuery>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.ACCOUNT}`,
            method: API_METHODS.GET,
            params,
          };
        },
        providesTags: (res, _err, _) =>
          res?.data.map((con) => ({
            type: ENDPOINT_URLS.ACCOUNT,
            id: con._id,
          })) ?? [],
      }),

      getAllAccountUsers: builder.query<Array<User>, string>({
        query: (id) => `${ENDPOINT_URLS.ACCOUNT}/${id}/users`,
      }),

      getAccountTags: builder.query<
        string[],
        { applicationId?: string; applicationGroupId?: string }
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.ACCOUNT}/tags`,
          method: API_METHODS.GET,
          params,
        }),
        providesTags: [{ type: ENDPOINT_URLS.ACCOUNT, id: 'TAGS' }],
      }),

      createAccount: builder.mutation<Account, CreateDto<Account>>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.ACCOUNT}`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.ACCOUNT],
      }),

      updateAccount: builder.mutation<
        Account,
        { id: string; data: UpdateDto<Account> }
      >({
        query: ({ id, data }) => ({
          url: `${ENDPOINT_URLS.ACCOUNT}/${id}`,
          method: API_METHODS.PATCH,
          body: data,
        }),
        invalidatesTags: (_res, _err, { id }) => [
          {
            type: ENDPOINT_URLS.ACCOUNT,
            id,
          },
          { type: ENDPOINT_URLS.ACCOUNT, id: 'TAGS' },
        ],
      }),

      bulkRenameAccounts: builder.mutation<
        BulkRenameResult[],
        BulkRenameItem[]
      >({
        query: (items) => ({
          url: `${ENDPOINT_URLS.ACCOUNT}/bulk-rename`,
          method: API_METHODS.PATCH,
          body: { items },
        }),
        invalidatesTags: (_res, _err, items) =>
          items.map(({ id }) => ({ type: ENDPOINT_URLS.ACCOUNT, id })),
      }),

      /** Deletes each item with its own request, reporting per-item results. */
      bulkDeleteAccounts: builder.mutation<BulkItemResult[], BulkItem[]>({
        queryFn: async (items, _api, _extra, baseQuery) => ({
          data: await settleEach(items, ({ id }) =>
            baseQuery({
              url: `${ENDPOINT_URLS.ACCOUNT}/${id}`,
              method: API_METHODS.DELETE,
            })
          ),
        }),
        invalidatesTags: (_res, _err, items) => [
          ...items.map(({ id }) => ({ type: ENDPOINT_URLS.ACCOUNT, id })),
          { type: ENDPOINT_URLS.ACCOUNT, id: 'TAGS' },
        ],
      }),

      deleteAccount: builder.mutation<{ success: boolean }, string>({
        query: (id) => ({
          url: `${ENDPOINT_URLS.ACCOUNT}/${id}`,
          method: API_METHODS.DELETE,
        }),
        invalidatesTags: (_res, _err, id) => [
          {
            type: ENDPOINT_URLS.ACCOUNT,
            id,
          },
          { type: ENDPOINT_URLS.ACCOUNT, id: 'TAGS' },
        ],
      }),

      getAllPrePaidDetails: builder.query<
        AggregateAccountDetailsQueryOutput[],
        AggregateAccountDetailsQuery
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.ACCOUNT}/prepaid-acc-details`,
            method: API_METHODS.GET,
            params,
          };
        },
      }),

      linkUserByEmail: builder.mutation<Account, LinkUserByEmailInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.ACCOUNT}/link-user-by-email`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: (_res, _err, { accountId }) => [
          {
            type: ENDPOINT_URLS.ACCOUNT,
            id: accountId,
          },
        ],
      }),
    }),
  });

export const {
  useGetAccountByIdQuery,
  useGetAllAccountsQuery,
  useLazyGetAllAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useBulkRenameAccountsMutation,
  useDeleteAccountMutation,
  useBulkDeleteAccountsMutation,
  useGetAllAccountUsersQuery,
  useGetAccountTagsQuery,
  useGetAllPrePaidDetailsQuery,
  useLinkUserByEmailMutation,
} = accountAPI;

setToast('bulkRenameAccounts', {
  bulkResults: { noun: 'account', verb: 'rename' },
});
setToast('bulkDeleteAccounts', {
  bulkResults: { noun: 'account', verb: 'delete' },
});
